import React, { useState } from "react";
import { KeyRound, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { useLang } from "../i18n";

const ROLES = ["admin", "operator", "auditor", "client", "provider"];

export const PlatformUserActions = ({ user, index, onChanged, tenants = null }) => {
  const { t } = useLang();
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async (fn, okMsg, close) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); close(); onChanged?.(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const save = (e) => {
    e.preventDefault();
    const payload = Object.fromEntries(Object.entries(edit).filter(([k, v]) => v !== "" && v !== null && (user.role !== "superadmin" || !["role", "tenant_id"].includes(k))));
    run(() => api.patch(`/platform/users/${user.id}`, payload), t("u.updated"), () => setEdit(null));
  };
  const remove = () => run(() => api.delete(`/platform/users/${user.id}`), t("u.deleted"), () => setConfirm(false));
  const reset = () => run(() => api.post(`/platform/users/${user.id}/reset-password`), `${t("pu.reset_sent")} ${user.email}`, () => {});

  const ib = "p-1.5 rounded-md transition-colors disabled:opacity-50";
  return (
    <>
      <div className="flex items-center justify-end gap-1" data-testid={`pu-actions-${index}`}>
        <button type="button" title={t("u.edit")} disabled={busy} data-testid={`pu-edit-${index}`} className={`${ib} text-[#044CBC] hover:bg-[#EFF4FC]`}
          onClick={() => setEdit({ name: user.name || "", email: user.email || "", role: user.role || "operator", active: user.active !== false, tenant_id: user.tenant_id || "" })}><Pencil className="w-4 h-4" /></button>
        <button type="button" title={t("pu.reset")} disabled={busy} data-testid={`pu-reset-${index}`} className={`${ib} text-amber-600 hover:bg-amber-50`} onClick={reset}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}</button>
        <button type="button" title={t("u.delete")} disabled={busy} data-testid={`pu-delete-${index}`} className={`${ib} text-red-600 hover:bg-red-50`} onClick={() => setConfirm(true)}><Trash2 className="w-4 h-4" /></button>
      </div>

      {edit && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="pu-edit-modal" onClick={() => setEdit(null)}>
          <form onSubmit={save} className="ec-card p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-slate-900 flex items-center gap-2"><Pencil className="w-5 h-5 text-[#044CBC]" /> {t("pu.edit_title")}</h3>
            <div><label className="ec-label">{t("u.name")}</label><input className="ec-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} required minLength={2} data-testid="pu-edit-name" /></div>
            <div><label className="ec-label">{t("u.email")}</label><input className="ec-input" type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} required data-testid="pu-edit-email" /></div>
            <div><label className="ec-label">{t("pu.new_temp_password")}</label><input className="ec-input" type="text" value={edit.password || ""} onChange={(e) => setEdit({ ...edit, password: e.target.value })} minLength={8} autoComplete="off" placeholder={t("pu.leave_blank")} data-testid="pu-edit-password" /></div>
            {user.role !== "superadmin" && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="ec-label">{t("u.role")}</label>
                <select className="ec-input" value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} data-testid="pu-edit-role">
                  {ROLES.map((r) => <option key={r} value={r}>{t(`u.${r}`)}</option>)}
                </select></div>
              <div><label className="ec-label">{t("u.status")}</label>
                <select className="ec-input" value={edit.active ? "1" : "0"} onChange={(e) => setEdit({ ...edit, active: e.target.value === "1" })} data-testid="pu-edit-active">
                  <option value="1">{t("u.active")}</option><option value="0">{t("u.inactive")}</option>
                </select></div>
            </div>
            )}
            {user.role === "superadmin" && (
              <div><label className="ec-label">{t("u.status")}</label>
                <select className="ec-input" value={edit.active ? "1" : "0"} onChange={(e) => setEdit({ ...edit, active: e.target.value === "1" })} data-testid="pu-edit-active">
                  <option value="1">{t("u.active")}</option><option value="0">{t("u.inactive")}</option>
                </select></div>
            )}
            {tenants && user.role !== "superadmin" && (
              <div><label className="ec-label">{t("ah.tenant")}</label>
                <select className="ec-input" value={edit.tenant_id} onChange={(e) => setEdit({ ...edit, tenant_id: e.target.value })} data-testid="pu-edit-tenant">
                  {tenants.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select></div>
            )}
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={busy} data-testid="pu-edit-save" className="flex-1 px-4 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 inline-flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("common.save")}</button>
              <button type="button" onClick={() => setEdit(null)} data-testid="pu-edit-cancel" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">{t("u.cancel")}</button>
            </div>
          </form>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="pu-delete-modal" onClick={() => setConfirm(false)}>
          <div className="ec-card p-6 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-slate-900 flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-600" /> {t("u.delete")}</h3>
            <p className="text-sm text-slate-600">{t("pu.delete_confirm")} <b data-testid="pu-delete-email">{user.email}</b></p>
            <div className="flex gap-3">
              <button type="button" onClick={remove} disabled={busy} data-testid="pu-delete-confirm" className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60 inline-flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("common.delete")}</button>
              <button type="button" onClick={() => setConfirm(false)} data-testid="pu-delete-cancel" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">{t("u.cancel")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
